import { useEffect, useRef, useState } from "react";

interface AnimatedImageProps {
  src: string;
  alt: string;
  className?: string;
  delay?: number;
  fadeOnly?: boolean;
  fill?: boolean;
}

const AnimatedImage = ({ src, alt, className = "", delay = 0, fadeOnly = false, fill = false }: AnimatedImageProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15, rootMargin: "50px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const visible = isInView && (isLoaded || hasError);

  // Sizing: when `fill` is true the image fills the parent (parent must have height).
  // When `fill` is false, the image determines its own height (block, full width).
  const sizingClasses = fill
    ? "w-full h-full object-cover"
    : "w-full h-auto block";

  return (
    <div ref={ref} className={`relative w-full ${fill ? "h-full" : ""} overflow-hidden rounded-xl`}>
      {/* Placeholder */}
      <div
        className={`absolute inset-0 bg-cream-200 animate-pulse transition-opacity duration-500 ${visible ? "opacity-0" : "opacity-100"}`}
      />
      {/* Image */}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onLoad={() => setIsLoaded(true)}
        onError={() => setHasError(true)}
        className={`${fadeOnly ? "img-fade" : "img-reveal"} ${visible ? "visible" : ""} ${sizingClasses} ${className}`}
        style={{ transitionDelay: `${delay}ms` }}
      />
    </div>
  );
};

export default AnimatedImage;
